import React, { useEffect, useState } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import {
  consumerClientContext,
  QUERY_GET_RECOMMENDED_PRODUCTS,
  useShopClient
} from 'shop/client'
import { CommonTitle, thinScrollbar } from '../common'
import { Button } from '../Controls'
import CenterModal from './CenterModal'
import { CategoryProduct } from '../Shop/Categories'
import { GetRecommendedProductsResponse } from 'shop/types/cart'
import { ProductCard } from '../Product'
import {
  useConsumerCart,
  useMediaQueries,
  useModal,
  useSessionStorage
} from 'shop/hooks'
import BottomModal from './BottomModal'

export type UpsellModalProps = {
  closeUpsellModal: () => void
}

const UpsellModal = ({ closeUpsellModal }: UpsellModalProps) => {
  const client = useShopClient()
  const navigate = useNavigate()
  const { closeModal } = useModal()
  const { isMobile } = useMediaQueries()
  const { setSessionStorageItem } = useSessionStorage('upsell-modal')
  const { isModalOpen } = useModal()

  const [upsellProducts, setUpsellProducts] = useState<CategoryProduct[]>([])
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true)

  const handleNavToCheckout = () => {
    closeUpsellModal()
    closeModal('cart')
    navigate('/checkout')
  }

  useEffect(() => {
    setLoadingProducts(true)
    client
      .query<GetRecommendedProductsResponse>({
        query: QUERY_GET_RECOMMENDED_PRODUCTS,
        ...consumerClientContext,
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        if (!!data.cart.recommendedProducts.length) {
          setUpsellProducts(data.cart.recommendedProducts)
          setSessionStorageItem('viewed')
        } else {
          // if no products returned, just continue to /checkout
          handleNavToCheckout()
        }
      })
      .catch(() => {
        // if call fails, just continue to /checkout
        handleNavToCheckout()
      })
      .finally(() => {
        setLoadingProducts(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loadingProducts) return <></>

  // Render different Modals based upon device
  return isMobile ? (
    <BottomModal
      handleCloseModal={closeUpsellModal}
      canClose={true}
      inModal={isModalOpen('cart')}
      modalHeight={'100%'}
    >
      <UpsellModalContent
        upsellProducts={upsellProducts}
        setUpsellProducts={setUpsellProducts}
        handleNavToCheckout={handleNavToCheckout}
      />
    </BottomModal>
  ) : (
    <CenterModal
      handleCloseModal={closeUpsellModal}
      styles={{ maxWidth: '560px' }}
      modalButtonVariant={'upsell'}
      inModal={isModalOpen('cart')}
    >
      <UpsellModalContent
        upsellProducts={upsellProducts}
        setUpsellProducts={setUpsellProducts}
        handleNavToCheckout={handleNavToCheckout}
      />
    </CenterModal>
  )
}

type UpsellModalContentProps = {
  upsellProducts: CategoryProduct[]
  setUpsellProducts: React.Dispatch<React.SetStateAction<CategoryProduct[]>>
  handleNavToCheckout: () => void
}

const UpsellModalContent = ({
  upsellProducts,
  setUpsellProducts,
  handleNavToCheckout
}: UpsellModalContentProps) => {
  const { cart } = useConsumerCart()

  // list of product ids that have been added since displaying the UpsellModal
  const [productsAdded, setProductsAdded] = useState<CategoryProduct['id'][]>(
    []
  )

  // Listen to cart updates and create a list of products that have been added from the upsell.
  useEffect(() => {
    if (!cart?.orderItems.length) return

    const upsellProductsInCart = cart.orderItems
      .filter(({ product }) =>
        upsellProducts.some((upsellItem) => upsellItem.id === product.id)
      )
      .map(({ product }) => product.id)
    setProductsAdded(upsellProductsInCart)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.orderItems])

  // Remove the added products from the original list of upsell products after a delay.
  useEffect(() => {
    if (!!productsAdded.length) {
      setTimeout(() => {
        const updatedUpsellProducts = upsellProducts.filter(
          (upsellProduct) =>
            !productsAdded.some(
              (addedProductId) => addedProductId === upsellProduct.id
            )
        )

        // no more products to add, nav to checkout
        if (updatedUpsellProducts.length === 0) {
          handleNavToCheckout()
        }
        setUpsellProducts(updatedUpsellProducts)
      }, 1000)
    }
  }, [productsAdded])

  return (
    <Container>
      <HeaderSection>
        <HeaderContent>
          <CommonTitle>Top Picks Customers Love</CommonTitle>
        </HeaderContent>
      </HeaderSection>
      <ProductsSection>
        <ProductsList>
          {upsellProducts.map((product, index) => {
            const hasBeenAdded = productsAdded.includes(product.id)

            return (
              <ProductsListItem key={product.slug} hasBeenAdded={hasBeenAdded}>
                <ProductAddedCover hasBeenAdded={hasBeenAdded} />
                <ProductCard
                  product={product}
                  productCategory={'upsell'}
                  isLast={index === upsellProducts.length - 1}
                  isStoreAcceptingOrders={true}
                  source='MOST_POPULAR_UPSELL_MODAL'
                />
              </ProductsListItem>
            )
          })}
        </ProductsList>
      </ProductsSection>
      <ModalActionsSection>
        <AllergenInfo>
          Allergen information can be found on our menu.
        </AllergenInfo>
        <Button onClick={handleNavToCheckout}>Go to checkout</Button>
      </ModalActionsSection>
    </Container>
  )
}

const Container = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    height: '100%',

    [theme.mediaQueries.viewport7]: {
      maxHeight: '90vh'
    }
  })
)

const HeaderSection = styled.div(
  ({ theme }): CSSObject => ({
    padding: '16px',
    borderBottom: '1px solid #D9D9D9',
    position: 'relative',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 10,

    [theme.mediaQueries.viewport7]: {
      position: 'initial'
    }
  })
)

const HeaderContent = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginRight: '20%'
  })
)

const ProductsSection = styled.div(
  ({ theme }): CSSObject => ({
    overflow: 'auto',
    padding: '4px 16px',
    backgroundColor: '#fff',
    ...(thinScrollbar(theme) as any),
    height: '100%',

    [theme.mediaQueries.viewport7]: {
      padding: '16px 24px',
      maxHeight: '600px'
    }
  })
)

const ProductsList = styled.ul(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    margin: 0,

    [theme.mediaQueries.viewport7]: {
      gap: '16px'
    }
  })
)

const ProductsListItem = styled.li<{ hasBeenAdded: boolean }>(
  ({ theme, hasBeenAdded }): CSSObject => ({
    position: 'relative',
    height: hasBeenAdded ? 0 : '141px',
    opacity: hasBeenAdded ? 0 : 1,
    overflow: 'hidden',

    transition: 'all 0.3s ease-in',
    transitionDelay: '0.6s',

    [theme.mediaQueries.viewport7]: {
      height: hasBeenAdded ? 0 : '154px'
    },

    '& a': {
      zIndex: 1,
      pointerEvents: hasBeenAdded ? 'none' : 'auto'
    }
  })
)
const ProductAddedCover = styled.div<{ hasBeenAdded: boolean }>(
  ({ theme, hasBeenAdded }): CSSObject => ({
    display: 'flex',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: hasBeenAdded ? 2 : 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAFAFA',
    opacity: hasBeenAdded ? 0.9 : 0,
    transition: `opacity 0.3s ease-in`
  })
)

const ModalActionsSection = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    zIndex: 10,
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    borderTop: '1px solid #D9D9D9',

    [theme.mediaQueries.viewport7]: {
      position: 'initial'
    }
  })
)

const AllergenInfo = styled.p(
  ({ theme }): CSSObject => ({
    fontSize: '14px',
    color: '#595959',
    margin: 0
  })
)

export default UpsellModal
